import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';

import { cn } from '@/lib/utils';
import { BarList, DistributionBar, MeterBar, MiniStat, ScoreRing, scoreTone } from '../../../components/design-system/analytics';
import { KpiCard, SectionCard, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import { MarketData, MarketKeyword } from '../../../common/hooks/use-market-api.hook';
import { classifyAudit } from '../../../modules/vendor/market/health-checks';
import { ROUTES, searchSegment } from '../../../modules/vendor/market/insight-spec';
import {
    authorityScore,
    bestKeywords,
    closestRival,
    historyChange,
    historyTrend,
    mergeGaps,
    pageTwoCount,
    quickWins,
    rankingBands,
    youOf,
} from '../../../modules/vendor/market/market-analysis';
import { marketSummary, overviewInsights, rivalReading } from '../../../modules/vendor/market/market-insights';
import { cleanDomain, monthLabel } from '../../../modules/vendor/market/market-presentation';
import { CompareBars } from './compare-bars';
import { Reading, Term, usd, useSpecText } from './market-shared';

/**
 * Everything search knows, on one page.
 *
 * Composed from the single market read the detail pages use, so it costs no
 * request and can never disagree with them. It says where the vendor stands
 * in a sentence, lists what to act on, then shows each part in miniature
 * with a link to the page that explains it.
 */
export const OverviewView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t, i18n } = useTranslation();
    const text = useSpecText();
    const o = data.overview;
    const keywords = useMemo(() => data.keywords?.keywords ?? [], [data.keywords]);

    const summary = useMemo(() => marketSummary(data), [data]);
    const specs = useMemo(() => overviewInsights(data), [data]);
    const history = data.history?.overview;
    const since = (key: string) => {
        const ch = historyChange(history, key);
        return ch ? { delta: { current: ch.current, previous: ch.previous }, since: t('BUSINESS.COMMON.SINCE', { month: monthLabel(ch.since, i18n.language) }) } : null;
    };
    const hint = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');

    const visits = since('estimatedMonthlyVisits');
    const top10 = since('top10');
    const value = since('trafficValue');
    const authority = authorityScore(data.backlinks?.rank);
    const authorityCh = historyChange(data.history?.backlinks, 'rank');

    return (
        <div className="flex flex-col gap-6">
            {summary.length > 0 && (
                <p className="max-w-[860px] text-[15px] leading-relaxed" data-testid="business-summary">
                    {summary.map((m) => text(m)).join(' ')}
                </p>
            )}

            <Reading specs={specs} />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                    label={t('BUSINESS.OVERVIEW.KPI.VISITS')}
                    value={formatNumber(o?.estimatedMonthlyVisits)}
                    delta={visits?.delta}
                    hint={hint([visits?.since, t('BUSINESS.OVERVIEW.KPI.VISITS_HINT')])}
                    trend={historyTrend(history, 'estimatedMonthlyVisits')}
                    href={ROUTES.search}
                />
                <KpiCard
                    label={t('BUSINESS.OVERVIEW.KPI.PAGE_ONE')}
                    value={formatNumber(o?.top10)}
                    delta={top10?.delta}
                    hint={hint([top10?.since, t('BUSINESS.OVERVIEW.KPI.PAGE_ONE_HINT', { total: formatNumber(o?.organicKeywords) })])}
                    trend={historyTrend(history, 'top10')}
                    href={searchSegment('page1')}
                />
                <KpiCard
                    label={t('BUSINESS.OVERVIEW.KPI.VALUE')}
                    value={o?.trafficValue != null ? usd(o.trafficValue) : '—'}
                    delta={value?.delta}
                    hint={hint([value?.since, t('BUSINESS.OVERVIEW.KPI.VALUE_HINT')])}
                    trend={historyTrend(history, 'trafficValue')}
                    href={ROUTES.search}
                />
                <KpiCard
                    label={t('BUSINESS.OVERVIEW.KPI.AUTHORITY')}
                    value={authority != null ? `${authority}` : '—'}
                    delta={authorityCh ? { current: authorityCh.current, previous: authorityCh.previous } : undefined}
                    hint={hint([
                        authorityCh ? t('BUSINESS.COMMON.SINCE', { month: monthLabel(authorityCh.since, i18n.language) }) : null,
                        t('BUSINESS.OVERVIEW.KPI.AUTHORITY_HINT'),
                    ])}
                    trend={historyTrend(data.history?.backlinks, 'rank')}
                    href={ROUTES.backlinks}
                />
            </div>

            <div className="grid gap-4 lg:grid-cols-5">
                <RankingCard data={data} className="lg:col-span-3" />
                <MovementCard data={data} className="lg:col-span-2" />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <SectionCard
                    title={<Term k="PAGE_TWO">{t('BUSINESS.OVERVIEW.QUICK_WINS')}</Term>}
                    description={t('BUSINESS.OVERVIEW.QUICK_WINS_HINT')}
                    action={<SeeAll to={searchSegment('quick')} />}
                >
                    <QuickWinList keywords={quickWins(keywords, 5)} />
                </SectionCard>
                <SectionCard title={t('BUSINESS.OVERVIEW.BEST')} description={t('BUSINESS.OVERVIEW.BEST_HINT')} action={<SeeAll to={ROUTES.search} />}>
                    <BarList
                        items={bestKeywords(keywords, 5)
                            .filter((k) => (k.estimatedVisits ?? 0) > 0)
                            .map((k, i) => ({
                                key: `${k.keyword}-${i}`,
                                label: k.keyword,
                                value: k.estimatedVisits ?? 0,
                                secondary: k.position != null ? `#${k.position}` : undefined,
                            }))}
                        emptyText={t('BUSINESS.OVERVIEW.NO_KEYWORDS')}
                    />
                    <p className="mt-2 text-xs text-muted-foreground">{t('BUSINESS.OVERVIEW.BEST_FOOT')}</p>
                </SectionCard>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <RivalCard data={data} />
                <HealthCard data={data} />
                <ProfileCard data={data} />
            </div>

            <GapTeaser data={data} />
        </div>
    );
};

const SeeAll: React.FC<{ to: string }> = ({ to }) => {
    const { t } = useTranslation();
    return (
        <Link to={to} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            {t('BUSINESS.COMMON.SEE_ALL')}
            <ArrowRight className="size-3 rtl:rotate-180" />
        </Link>
    );
};

/** The whole ranking distribution, with page two called out as the place to start. */
export const RankingCard: React.FC<{ data: MarketData; className?: string; showLink?: boolean }> = ({ data, className, showLink = true }) => {
    const { t } = useTranslation();
    const split = rankingBands(data.overview);
    const p2 = pageTwoCount(data.overview);
    return (
        <SectionCard
            className={className}
            title={<Term k="POSITION">{t('BUSINESS.RANKING.TITLE')}</Term>}
            description={t('BUSINESS.RANKING.HINT', { total: formatNumber(data.overview?.organicKeywords) })}
            action={showLink ? <SeeAll to={ROUTES.search} /> : undefined}
            testId="business-ranking"
        >
            {split ? (
                <div className="flex flex-col gap-5">
                    <DistributionBar
                        segments={split.bands.map((b) => ({ key: b.key, label: t(`BUSINESS.BANDS.${b.key.toUpperCase()}`), value: b.value }))}
                        columns={split.exact ? 6 : 3}
                    />
                    {p2 ? (
                        <Link
                            to={searchSegment('quick')}
                            className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/40"
                        >
                            <span className="mt-1.5 size-2 shrink-0 rounded-full bg-warning" aria-hidden />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm">{t('BUSINESS.RANKING.PAGE_TWO', { count: p2, n: formatNumber(p2) })}</span>
                                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{t('BUSINESS.RANKING.PAGE_TWO_HINT')}</span>
                            </span>
                            <ArrowRight className="mt-1 size-3.5 shrink-0 text-muted-foreground rtl:rotate-180" />
                        </Link>
                    ) : null}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">{t('BUSINESS.OVERVIEW.NO_KEYWORDS')}</p>
            )}
        </SectionCard>
    );
};

/** New, improved, declined, lost — since the previous look. */
export const MovementCard: React.FC<{ data: MarketData; className?: string }> = ({ data, className }) => {
    const { t } = useTranslation();
    const o = data.overview;
    const mv = o?.movement;
    const newCount = mv?.new ?? o?.newKeywords;
    const lostCount = mv?.lost ?? o?.lostKeywords;
    const rows = [
        { key: 'new', label: t('BUSINESS.MOVEMENT.NEW'), value: newCount, dot: 'bg-success', to: searchSegment('new') },
        { key: 'up', label: t('BUSINESS.MOVEMENT.UP'), value: mv?.up, dot: 'bg-success', to: searchSegment('up') },
        { key: 'down', label: t('BUSINESS.MOVEMENT.DOWN'), value: mv?.down, dot: 'bg-destructive', to: searchSegment('down') },
        { key: 'lost', label: t('BUSINESS.MOVEMENT.LOST'), value: lostCount, dot: 'bg-destructive', to: null },
    ].filter((r) => r.value != null);

    return (
        <SectionCard className={className} title={t('BUSINESS.MOVEMENT.TITLE')} description={t('BUSINESS.MOVEMENT.HINT')} testId="business-movement">
            {rows.length ? (
                <div className="flex flex-col gap-5">
                    <div className="grid grid-cols-2 gap-4">
                        {rows.map((r) => {
                            const body = (
                                <MiniStat
                                    label={
                                        <span className="inline-flex items-center gap-1.5">
                                            <span className={cn('size-1.5 rounded-full', r.dot)} aria-hidden />
                                            {r.label}
                                        </span>
                                    }
                                    value={formatNumber(r.value)}
                                />
                            );
                            return r.to ? (
                                <Link key={r.key} to={r.to} className="-m-2 rounded-lg p-2 transition-colors hover:bg-accent/40">
                                    {body}
                                </Link>
                            ) : (
                                <div key={r.key}>{body}</div>
                            );
                        })}
                    </div>
                    {mv?.up != null && mv?.down != null && mv.up + mv.down > 0 && (
                        <div>
                            <p className="mb-2 text-xs text-muted-foreground">{t('BUSINESS.MOVEMENT.BALANCE')}</p>
                            <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-muted" aria-hidden>
                                <div className="h-full bg-success" style={{ width: `${(mv.up / (mv.up + mv.down)) * 100}%` }} />
                                <div className="h-full bg-destructive" style={{ width: `${(mv.down / (mv.up + mv.down)) * 100}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">{t('BUSINESS.MOVEMENT.NONE')}</p>
            )}
        </SectionCard>
    );
};

/** Page-two searches as a short list: the search, where you are, how many make it, how hard. */
export const QuickWinList: React.FC<{ keywords: MarketKeyword[] }> = ({ keywords }) => {
    const { t } = useTranslation();
    if (!keywords.length) return <p className="text-sm text-muted-foreground">{t('BUSINESS.OVERVIEW.NO_QUICK_WINS')}</p>;
    return (
        <ul className="flex flex-col divide-y">
            {keywords.map((k, i) => (
                <li key={`${k.keyword}-${i}`} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                    <span className="w-9 shrink-0 text-sm font-medium tabular-nums">#{k.position}</span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm">{k.keyword}</span>
                        <span className="block text-xs text-muted-foreground tabular-nums">
                            {t('BUSINESS.COMMON.PER_MONTH', { n: formatNumber(k.searchVolume) })}
                        </span>
                    </span>
                    <MeterBar value={k.difficulty} invert width="w-12" />
                </li>
            ))}
        </ul>
    );
};

const RivalCard: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const text = useSpecText();
    const rival = closestRival(data);
    const you = youOf(data);
    return (
        <SectionCard title={t('BUSINESS.OVERVIEW.CLOSEST')} description={t('BUSINESS.OVERVIEW.CLOSEST_HINT')} action={<SeeAll to={ROUTES.rivals} />}>
            {rival ? (
                <div className="flex flex-col gap-4">
                    <div>
                        <p className="truncate text-sm font-medium">{cleanDomain(rival.domain)}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                            {rivalReading(rival, you)
                                .slice(0, 2)
                                .map((m) => text(m))
                                .join(' · ')}
                        </p>
                    </div>
                    <CompareBars
                        rival={cleanDomain(rival.domain)}
                        rows={[
                            { key: 'kw', label: t('BUSINESS.COMPARE.KEYWORDS'), you: you.keywords, them: rival.organicKeywords },
                            { key: 'visits', label: t('BUSINESS.COMPARE.VISITS'), you: you.visits, them: rival.estimatedMonthlyVisits },
                            { key: 'top10', label: t('BUSINESS.COMPARE.PAGE_ONE'), you: you.top10, them: rival.top10 },
                        ]}
                    />
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">{t('MARKET.OVERVIEW.NO_RIVALS')}</p>
            )}
        </SectionCard>
    );
};

const HealthCard: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const audit = data.pageAudit;
    const { counts } = classifyAudit(audit);
    return (
        <SectionCard title={t('BUSINESS.OVERVIEW.HEALTH')} description={t('BUSINESS.OVERVIEW.HEALTH_HINT')} action={<SeeAll to={ROUTES.health} />}>
            {audit ? (
                <div className="flex flex-col gap-4">
                    <ScoreRing
                        value={audit.score ?? null}
                        size={84}
                        tone={scoreTone(audit.score, { good: 80, bad: 60 })}
                        label={t('BUSINESS.HEALTH.SCORE_LABEL')}
                        sublabel={t('BUSINESS.HEALTH.COUNTS', { errors: formatNumber(counts.error), warnings: formatNumber(counts.warning) })}
                    />
                    {data.screenshot?.imageUrl && (
                        <img src={data.screenshot.imageUrl} alt="" className="h-24 w-full rounded-lg border object-cover object-top" />
                    )}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">{t('MARKET.HEALTH.NONE')}</p>
            )}
        </SectionCard>
    );
};

const ProfileCard: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const b = data.business;
    return (
        <SectionCard title={t('BUSINESS.OVERVIEW.PROFILE')} description={t('BUSINESS.OVERVIEW.PROFILE_HINT')} action={<SeeAll to={ROUTES.profile} />}>
            {!b ? (
                <p className="text-sm text-muted-foreground">{t('MARKET.PROFILE.NONE')}</p>
            ) : b.notFound ? (
                <StatusBadge tone="danger">{t('MARKET.PROFILE.MISSING')}</StatusBadge>
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="flex items-baseline gap-2">
                        <span className="font-heading text-3xl leading-none font-medium tabular-nums">{b.rating ? formatNumber(b.rating, 1) : '—'}</span>
                        <Stars value={b.rating} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {t('BUSINESS.PROFILE.REVIEWS_COUNT', { count: b.totalReviews ?? b.reviews ?? 0, n: formatNumber(b.totalReviews ?? b.reviews ?? 0) })}
                    </p>
                    <StatusBadge tone={b.isClaimed ? 'success' : 'danger'}>
                        {t(b.isClaimed ? 'MARKET.PROFILE.CLAIMED_YES' : 'MARKET.PROFILE.CLAIMED_NO')}
                    </StatusBadge>
                </div>
            )}
        </SectionCard>
    );
};

/** Five stars, filled to the rating. Ink, not gold: the number carries the meaning. */
export const Stars: React.FC<{ value?: number | null; className?: string }> = ({ value, className }) => {
    if (value == null) return null;
    return (
        <span className={cn('inline-flex items-center gap-0.5', className)} aria-hidden>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={cn('size-3.5', value >= i - 0.25 ? 'fill-foreground text-foreground' : 'text-muted-foreground/40')} />
            ))}
        </span>
    );
};

/** The best three searches a competitor wins and the vendor does not. */
const GapTeaser: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t } = useTranslation();
    const rows = useMemo(() => mergeGaps(data.keywordGaps).filter((r) => !r.brand).slice(0, 3), [data.keywordGaps]);
    if (!rows.length) return null;
    return (
        <SectionCard title={t('BUSINESS.OVERVIEW.GAPS')} description={t('BUSINESS.OVERVIEW.GAPS_HINT')} action={<SeeAll to={ROUTES.gaps} />}>
            <ul className="grid gap-3 sm:grid-cols-3">
                {rows.map((r) => (
                    <li key={r.keyword} className="flex flex-col gap-2 rounded-lg border p-3">
                        <span className="line-clamp-2 text-sm font-medium">{r.keyword}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                            {t('BUSINESS.COMMON.PER_MONTH', { n: formatNumber(r.searchVolume) })} · {r.rivals.map((x) => x.rival).join(', ')}
                        </span>
                        <MeterBar value={r.score} label={t('BUSINESS.GAPS.SCORE_SHORT', { score: r.score })} width="w-20" />
                    </li>
                ))}
            </ul>
        </SectionCard>
    );
};
